import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import OnboardingBottomNav from '../../components/common/OnboardingBottomNav';
import '../../styles/onboarding/Onboarding.css';

const KidsBirthday = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();  // Add this line to get the groupId from URL params
  const [children, setChildren] = useState([
    { id: 1, name: '', birthdate: '' }
  ]);

  useEffect(() => {
    // Load any previously saved children data if available
    const savedChildren = localStorage.getItem('children');
    if (savedChildren) {
      try {
        setChildren(JSON.parse(savedChildren));
      } catch (error) {
        console.error('Error parsing saved children data:', error);
      }
    }
  }, []);

  const addAnotherChild = () => {
    if (children.length < 15) {
      const newChildId = children.length + 1;
      setChildren([...children, { id: newChildId, name: '', birthdate: '' }]);
    }
  };

  const handleInputChange = (id, field, value) => {
    const updatedChildren = children.map(child => 
      child.id === id ? { ...child, [field]: value } : child
    );
    setChildren(updatedChildren);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    // If dateString is already in yyyy-MM-dd format, return it
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    
    // Try to convert MM/DD/YYYY to yyyy-MM-dd
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    return dateString;
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    
    // Convert yyyy-MM-dd to MM/DD/YYYY for display
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${month}/${day}/${year}`;
    }
    
    return dateString;
  };

  const handleDateChange = (id, value) => {
    // Store date in yyyy-MM-dd format internally
    handleInputChange(id, 'birthdate', value);
  };

  const handleNext = () => {
    // Save children data to local storage or context
    const validChildren = children.filter(child => child.name.trim() !== '' || child.birthdate.trim() !== '');
    localStorage.setItem('children', JSON.stringify(validChildren));
    
    // Navigate to the Yartzheits page with groupId
    navigate(`/group/${groupId}/onboarding/yartzheits`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSkip = () => {
    // Navigate to Yartzheits page when skipping, with groupId
    navigate(`/group/${groupId}/onboarding/yartzheits`);
  };

  // Check if form is valid
  const isFormValid = children.some(child => 
    child.name.trim() !== '' && child.birthdate.trim() !== ''
  );

  // Progress dots renderer
  const renderProgressDots = () => {
    const totalSteps = 5; // Total steps including yartzheits
    const currentStep = 4; // Kids page is the 4th step
    const dots = [];
    
    for (let i = 0; i < totalSteps; i++) {
      dots.push(
        <div 
          key={i} 
          className={`progress-dot ${i + 1 <= currentStep ? 'active' : ''}`}
        ></div>
      );
    }
    
    return dots;
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-content">
        <h1 className="kids-title">You're Kids To!</h1>
        <p className="kids-description">
          Your shul doesn't want to miss your important dates—yes, even your kids. Don't make us guess their names at the next Kiddush!
        </p>

        <div className="kids-form">
          {children.map((child) => (
            <div key={child.id} className="child-details">
              <h2 className="child-label">Child {child.id}</h2>
              <div className="child-input-group">
                <input
                  type="text"
                  placeholder="Name"
                  value={child.name}
                  onChange={(e) => handleInputChange(child.id, 'name', e.target.value)}
                  className="form-input"
                />
                <input
                  type="date"
                  placeholder="Birth Date"
                  value={formatDate(child.birthdate)}
                  onChange={(e) => handleDateChange(child.id, e.target.value)}
                  className="form-input date-input"
                  max={new Date().toISOString().split('T')[0]} // Prevent future dates
                />
              </div>
            </div>
          ))}
          
          {children.length < 15 && (
            <button 
              className="add-child-button" 
              onClick={addAnotherChild}
            >
              Add another child
            </button>
          )}
        </div>
      </div>

      {/* Progress dots */}
      <div className="fixed-progress-dots">
        {renderProgressDots()}
      </div>

      {/* Skip button */}
      <div className="skip-button-container">
        <button className="skip-button" onClick={handleSkip}>
          Skip
        </button>
      </div>

      {/* Use the OnboardingBottomNav component for consistent navigation */}
      <OnboardingBottomNav
        onBack={handleBack}
        onNext={handleNext}
        nextDisabled={!isFormValid}
        nextLabel="Next"
      />
    </div>
  );
};

export default KidsBirthday;